import Box from "@/common/components/Box"
import Button from "@/common/components/Button"
import { ChangeHistory } from "@/common/components/ChangeHistory"
import { FormTransformers } from "@/common/utils/formTransformers"
import { AccreditationInformationSection } from "@/features/establishmentLeadsFunnel/components/AccreditationInformationSection"
import { AddressSection } from "@/features/establishmentLeadsFunnel/components/AddressSection"
import { BankAccountInformationSection } from "@/features/establishmentLeadsFunnel/components/BankAccountInformationSection"
import { ContactsSection } from "@/features/establishmentLeadsFunnel/components/ContactsSection"
import { GeneralInformationSection } from "@/features/establishmentLeadsFunnel/components/GeneralInformationSection"
import { GenericDocumentsSection } from "@/features/establishmentLeadsFunnel/components/GenericDocumentsSection"
import { LeadHeader } from "@/features/establishmentLeadsFunnel/components/LeadHeader"
import { ResponsibleSection } from "@/features/establishmentLeadsFunnel/components/ResponsibleSection"
import { stateStrategy, translateStateStrategy } from "@/features/establishmentLeadsFunnel/constants/stateStrategy.constant"
import { useAdvanceEstablishmentLead } from "@/features/establishmentLeadsFunnel/hooks/useAdvanceEstablishmentLead"
import { useEstablishmentLeadForm } from "@/features/establishmentLeadsFunnel/hooks/useEstablishmentLeadForm"
import useRegressLead from "@/features/establishmentLeadsFunnel/hooks/useRegressLead"
import useReviewLead from "@/features/establishmentLeadsFunnel/hooks/useReviewLead"
import { EstablishmentLeadsFunnelUpdateValidationSchema } from "@/features/establishmentLeadsFunnel/schemas/update.schema"
import { EstablishmentLeadData } from "@/features/establishmentLeadsFunnel/types/establishmentLead.type"
import { Checklist } from "@/features/privateCompanyLeadsFunnel/components/Checklist"
import { useIsMutating, useQueryClient } from "@tanstack/react-query"
import { FormProvider, SubmitHandler } from "react-hook-form"
import { useNavigate } from "react-router-dom"
import { useUpdateEstablishmentLeadManagers } from "../../hooks/useUpdateEstablishmentLeadManagers"
import { useUpdateEstablishmentLead } from "../../hooks/useUpdateEstablishmentLead"

export const LeadDeployingManagementReviewForm = ({
  id,
  establishmentLead,
}: {
  id: string,
  establishmentLead: EstablishmentLeadData,
}) => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const isMutating = !!useIsMutating()

  const passedEveryRequirement = establishmentLead?.requirement_list_summary?.steps?.every((step) => step.passed)

  const hasDisapprovedReview = establishmentLead?.last_review?.state === 'disapproved'

  const updateLeadOnSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['establishmentLead', id] })
  }

  const redirectOnSuccess = (path: string) => {
    queryClient.invalidateQueries({ queryKey: ['establishmentLead', id] })
    navigate(path)
  }

  const {
    mutate: approveEstablishmentLead,
    isPending: approveEstablishmentLeadIsPending,
    isError: approveEstablishmentLeadIsError,
  } = useReviewLead({
    establishmentLeadId: id,
    state: 'approved',
    onSuccess: updateLeadOnSuccess,
  })

  const {
    mutate: declineEstablishmentLead,
    isPending: declineEstablishmentLeadIsPending,
    isError: declineEstablishmentLeadIsError,
  } = useReviewLead({
    establishmentLeadId: id,
    state: 'disapproved',
    onSuccess: updateLeadOnSuccess,
  })

  const {
    mutate: advanceEstablishmentLead,
    isPending: advanceEstablishmentLeadIsPending,
    isError: advanceEstablishmentLeadIsError,
  } = useAdvanceEstablishmentLead({
    id,
    onSuccess: () => redirectOnSuccess('/establishment-leads'),
  })

  const {
    mutate: regressEstablishmentLead,
    isPending: regressEstablishmentLeadIsPending,
    isError: regressEstablishmentLeadIsError,
  } = useRegressLead({
    id,
    onSuccess: () => redirectOnSuccess('/establishment-leads'),
  })

  const formMethods = useEstablishmentLeadForm({ establishmentLead })

  const {
    control,
    register,
    setError,
    getValues,
    handleSubmit,
    formState: { errors },
  } = formMethods

  const {
    mutate: updateEstablishmentLeadManagers,
    isPending: updateEstablishmentLeadManagersIsPending,
  } = useUpdateEstablishmentLeadManagers({
    id,
    onSuccess: updateLeadOnSuccess,
    onError: FormTransformers.errorsTransformer<EstablishmentLeadsFunnelUpdateValidationSchema>({ setError })
  })

  const {
    mutate: updateEstablishmentLead,
  } = useUpdateEstablishmentLead({
    id,
    establishmentLead,
    onSuccess: updateLeadOnSuccess,
    onError: FormTransformers.errorsTransformer<EstablishmentLeadsFunnelUpdateValidationSchema>({ setError })
  })


  const submitManagers = async () => {
    updateEstablishmentLeadManagers(getValues());
  };


  const onSubmit: SubmitHandler<EstablishmentLeadsFunnelUpdateValidationSchema> = (data) => updateEstablishmentLead(data)
  const submit = handleSubmit(onSubmit)

  const isServerPending = (
    approveEstablishmentLeadIsPending ||
    declineEstablishmentLeadIsPending ||
    advanceEstablishmentLeadIsPending ||
    regressEstablishmentLeadIsPending
  )

  const isServerError = (
    approveEstablishmentLeadIsError ||
    declineEstablishmentLeadIsError ||
    advanceEstablishmentLeadIsError ||
    regressEstablishmentLeadIsError
  )

  return (
    <FormProvider {...formMethods}>
      <form>
        <LeadHeader
          establishmentLeadId={id}
          display_name={establishmentLead.display_name}
          url={establishmentLead.general_information.company_logo?.url}
          showArchiveButton
          archived_at={establishmentLead.archived_at}
          buttons={
            <div className="flex gap-4">
              <Button
                type="button"
                variant={!hasDisapprovedReview ? 'yellow' : 'yellow-black'}
                disabled={!hasDisapprovedReview || isMutating}
                onClick={() => regressEstablishmentLead()}
              >
                Solicitar revisão
              </Button>
              <Button
                type="button"
                className={passedEveryRequirement ? "shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]" : ''}
                variant={passedEveryRequirement ? 'default' : 'outline-disabled'}
                disabled={!passedEveryRequirement || isServerPending || isMutating}
                onClick={() => advanceEstablishmentLead()}
              >
                Iniciar operação
              </Button>
            </div>
          }
        />
        <div className='flex gap-6'>
          <Box className="w-full flex flex-col gap-6 h-[calc(100vh-130px)] overflow-y-scroll">
            {isServerError && (
              <p className="text-sm text-danger-soft">Ocorreu um erro no servidor, cheque os campos e tente novamente</p>
            )}
            <ResponsibleSection
              establishmentLead={establishmentLead}
              submit={submitManagers}
              submitObservations={submit}
              last_review={establishmentLead.last_review}
              approveLead={approveEstablishmentLead}
              declineLead={declineEstablishmentLead}
              disabled={advanceEstablishmentLeadIsPending || regressEstablishmentLeadIsPending || updateEstablishmentLeadManagersIsPending}
            />
            <AccreditationInformationSection
              establishmentLeadId={id}
              establishmentLead={establishmentLead}
              contract={establishmentLead.contract}
              disabled
            />
            <GeneralInformationSection
              establishmentLeadId={id}
              general_information={establishmentLead.general_information}
              leadState={establishmentLead.state}
              control={control}
              register={register}
              errors={errors}
              disabled
            />
            <AddressSection
              establishmentLeadId={id}
              address={establishmentLead.address}
              leadState={establishmentLead.state}
              errors={errors}
              disabled
            />
            <BankAccountInformationSection
              bank_account_information={establishmentLead.bank_account_information}
              control={control}
              register={register}
              errors={errors}
              disabled
            />
            <ContactsSection
              establishmentLeadId={id}
              contacts={establishmentLead.contacts}
              leadState={establishmentLead.state}
              contact_for_master_user={establishmentLead.contact_for_master_user}
              control={control}
              showMasterUser
              disabled
            />
            <GenericDocumentsSection
              establishmentLeadId={id}
              generic_documents={establishmentLead.generic_documents}
              control={control}
              disabled
            />
          </Box>
          <div className="flex flex-col gap-4">
            <Checklist
              requirement_list_summary={establishmentLead.requirement_list_summary}
              state={translateStateStrategy[stateStrategy[establishmentLead.state as keyof typeof stateStrategy]]}
              Buttons={() => (
                <div className="flex flex-col gap-2">
                  <Button
                    type="button"
                    disabled={!hasDisapprovedReview}
                    variant={!hasDisapprovedReview ? 'yellow' : 'yellow-black'}
                    onClick={() => regressEstablishmentLead()}
                  >
                    Solicitar revisão
                  </Button>
                  <Button
                    type="button"
                    variant={passedEveryRequirement ? 'default' : 'outline-disabled'}
                    disabled={!passedEveryRequirement || isServerPending}
                    onClick={() => advanceEstablishmentLead()}
                  >
                    Iniciar operação
                  </Button>
                </div>
              )}
              className="flex flex-col gap-2 justify-between h-[calc((100vh-276px)/2)]"
            />
            <ChangeHistory logs={establishmentLead.logs || []} />
          </div>
        </div>
      </form>
    </FormProvider>
  )
}
